import { useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore, selectUser } from '../store/authStore';
import { authService } from '../services/auth';
import type { LoginRequest, SignupRequest } from '../types/auth';
import { dashboardKeys } from './useDashboard';
import { dashboardService } from '../services/dashboard';

export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
};

export const useLogin = () => {
  const queryClient = useQueryClient();
  const setAuth = useAuthStore.getState().setAuth;
  return useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: (data) => {
      if (data.token && data.user) setAuth(data.token, data.user);
      queryClient.setQueryData(authKeys.user(), data.user);
    },
    onError: () => {
      queryClient.removeQueries({ queryKey: authKeys.user() });
    },
  });
};

export const useSignup = () => {
  const queryClient = useQueryClient();
  const setAuth = useAuthStore.getState().setAuth;
  return useMutation({
    mutationFn: (userData: SignupRequest) => authService.signup(userData),
    onSuccess: (data) => {
      if (data.token && data.user) setAuth(data.token, data.user);
      queryClient.setQueryData(authKeys.user(), data.user);
    },
    onError: () => {
      queryClient.removeQueries({ queryKey: authKeys.user() });
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const logoutAction = useAuthStore.getState().logout;
  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      logoutAction();
      queryClient.removeQueries({ queryKey: authKeys.all });
    },
    onError: () => {
      logoutAction();
      queryClient.removeQueries({ queryKey: authKeys.all });
    },
  });
};

export const useUserDetails = (userId: string) => {
  const setUser = useAuthStore((s) => s.setUser);
  
  const query = useQuery({
    queryKey: dashboardKeys.userDetails(userId),
    queryFn: () => dashboardService.getUserDetails(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
  });

  useEffect(() => {
    if (query.data) {
      setUser(query.data);
    }
  }, [query.data, setUser]);

  return query;
};

/** Auth state and actions from Zustand store. Use this everywhere instead of context. */
export function useAuth() {
  const user = useAuthStore(selectUser);
  const token = useAuthStore((s) => s.token);
  const loading = useAuthStore((s) => s.loading);
  const error = useAuthStore((s) => s.error);
  const authChecked = useAuthStore((s) => s.authChecked);

  const setAuth = useAuthStore.getState().setAuth;
  const setLoading = useAuthStore.getState().setLoading;
  const setError = useAuthStore.getState().setError;
  const clearError = useAuthStore.getState().clearError;
  const logoutAction = useAuthStore.getState().logout;

  const isAuthenticated = !!token;

  const login = useCallback(
    async (credentials: LoginRequest) => {
      setLoading(true);
      clearError();
      try {
        const res = await authService.login(credentials);
        if (res.success && res.token && res.user) {
          setAuth(res.token, res.user);
        } else {
          throw new Error(res.message || 'Login failed');
        }
      } catch (err: any) {
        const message = err?.message || 'Login failed';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, clearError, setAuth, setError]
  );

  const signup = useCallback(
    async (userData: SignupRequest) => {
      setLoading(true);
      clearError();
      try {
        const res = await authService.signup(userData);
        if (res.success && res.token && res.user) {
          setAuth(res.token, res.user);
        } else {
          throw new Error(res.message || 'Signup failed');
        }
      } catch (err: any) {
        const message = err?.message || 'Signup failed';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, clearError, setAuth, setError]
  );

  const queryClient = useQueryClient();

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (e) {
      console.error('Logout API error:', e);
    } finally {
      queryClient.clear();
      logoutAction();
    }
  }, [logoutAction, queryClient]);

  const sendOtp = useCallback(
    async (email: string) => {
      clearError();
      try {
        const res = await authService.sendOtp(email);
        if (!res.success && res.success !== undefined) {
          throw new Error(res.message || 'Failed to send OTP');
        }
        return res;
      } catch (err: any) {
        const message = err?.message || 'Failed to send OTP';
        setError(message);
        throw err;
      }
    },
    [clearError, setError]
  );

  const verifyOtp = useCallback(
    async (email: string, otp: string) => {
      clearError();
      try {
        const res = await authService.verifyOtp(email, otp);
        if (!res.success && res.success !== undefined) {
          throw new Error(res.message || 'Invalid OTP');
        }
        return res;
      } catch (err: any) {
        const message = err?.message || 'Invalid OTP';
        setError(message);
        throw err;
      }
    },
    [clearError, setError]
  );

  return {
    user,
    token,
    isAuthenticated,
    loading,
    error,
    authChecked,
    login,
    signup,
    logout,
    sendOtp,
    verifyOtp,
    clearError,
  };
}

export { useAuthStore } from '../store/authStore';
